import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Collapse } from '@/shared/components/Collapse'
import {
  useGetMachineryDirectiveCompliance,
  useListStandardApplicability,
  buildStandardsHazardPreviewItems,
  type StandardApplicability,
  type StandardsHazardPreviewItem,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useListUserStandards } from '@/shared/hooks/api/userStandards'
import type { UserStandard } from '@/shared/hooks/api/userStandards'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { StandardDataStatusBadge } from '@/components/StandardDataStatusBadge'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'

function ExpandableItemDescription({
  description,
  showMoreLabel,
  showLessLabel,
}: {
  description: string
  showMoreLabel: string
  showLessLabel: string
}) {
  const [expanded, setExpanded] = useState(false)
  const [isClamped, setIsClamped] = useState(false)
  const descriptionRef = useRef<HTMLParagraphElement>(null)

  useLayoutEffect(() => {
    const element = descriptionRef.current
    if (element == null || expanded) {
      return
    }
    const updateIsClamped = () => {
      setIsClamped(element.scrollHeight > element.clientHeight + 1)
    }
    updateIsClamped()
    const observer = new ResizeObserver(updateIsClamped)
    observer.observe(element)
    return () => observer.disconnect()
  }, [description, expanded])

  const showToggle = expanded || isClamped

  return (
    <div>
      <p
        ref={descriptionRef}
        data-slot='item-description'
        className={cn(
          'text-left text-sm leading-normal font-normal text-muted-foreground',
          expanded ? 'line-clamp-none' : 'line-clamp-2'
        )}
      >
        {description}
      </p>
      {showToggle && (
        <button
          type='button'
          className='mt-1 text-sm text-primary hover:underline'
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            setExpanded((previous) => !previous)
          }}
        >
          {expanded ? showLessLabel : showMoreLabel}
        </button>
      )}
    </div>
  )
}

type CatalogueHazardExtraFieldsProps = {
  item: StandardsHazardPreviewItem
  fieldLabels: {
    examplesOfHazardousSituations: string
    associatedActivity: string
    relatedDangerZone: string
    clauseReference: string
  }
}

function CatalogueHazardExtraFields({
  item,
  fieldLabels,
}: CatalogueHazardExtraFieldsProps) {
  const rows: Array<{ label: string; value: string }> = []
  if (item.examplesOfHazardousSituations != null) {
    rows.push({
      label: fieldLabels.examplesOfHazardousSituations,
      value: item.examplesOfHazardousSituations,
    })
  }
  if (item.associatedActivity != null) {
    rows.push({
      label: fieldLabels.associatedActivity,
      value: item.associatedActivity,
    })
  }
  if (item.relatedDangerZone != null) {
    rows.push({
      label: fieldLabels.relatedDangerZone,
      value: item.relatedDangerZone,
    })
  }
  if (item.clauseReference != null) {
    rows.push({
      label: fieldLabels.clauseReference,
      value: item.clauseReference,
    })
  }
  if (rows.length === 0) {
    return null
  }
  return (
    <dl className='mt-2 space-y-1.5 text-sm'>
      {rows.map((row) => (
        <div key={row.label}>
          <dt className='font-medium text-foreground'>{row.label}</dt>
          <dd className='mt-0.5 whitespace-pre-wrap text-muted-foreground'>
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

function applicabilityNeedsDataBadge(sa: StandardApplicability): boolean {
  const hasData =
    sa.hasSignificantHazards === true ||
    sa.hasInformationForUse === true ||
    sa.hasRiskReductionMeasures === true
  return !hasData
}

function groupByStandard(
  items: StandardsHazardPreviewItem[]
): Map<string, StandardsHazardPreviewItem[]> {
  const map = new Map<string, StandardsHazardPreviewItem[]>()
  for (const item of items) {
    const ref = item.standardReference
    const list = map.get(ref) ?? []
    list.push(item)
    map.set(ref, list)
  }
  return map
}

export type GenerateFromStandardsModalProps = {
  isOpen: boolean
  onClose: () => void
  complianceId: string
  onGenerate: (selectedHazardIds: string[]) => void
  isGenerating?: boolean
  /** Non-empty significant hazard ids already generated (AGENT) for this compliance */
  generatedSignificantHazardIds?: ReadonlySet<string>
}

export function GenerateFromStandardsModal({
  isOpen,
  onClose,
  complianceId,
  onGenerate,
  isGenerating = false,
  generatedSignificantHazardIds = new Set(),
}: GenerateFromStandardsModalProps) {
  const { data: compliance } = useGetMachineryDirectiveCompliance(
    isOpen ? complianceId : undefined
  )
  const displayLocale = compliance?.contentLanguage
  const {
    data: applicability,
    isLoading: isLoadingApplicability,
    error: applicabilityError,
  } = useListStandardApplicability(complianceId)

  const acceptedApplicabilityWithUserStandard = useMemo(
    () =>
      applicability?.filter(
        (applicableStandard) =>
          applicableStandard.isAccepted === true &&
          applicableStandard.userStandardId != null
      ) ?? [],
    [applicability]
  )

  const { data: userStandardsList = [], isLoading: isLoadingUserStandards } =
    useListUserStandards({
      enabled: isOpen && acceptedApplicabilityWithUserStandard.length > 0,
    })

  const userStandardsById = useMemo(() => {
    const map = new Map(
      userStandardsList
        .filter((userStandard) =>
          acceptedApplicabilityWithUserStandard.some(
            (applicableStandard) =>
              applicableStandard.userStandardId === userStandard.id
          )
        )
        .map((userStandard) => [userStandard.id, userStandard])
    )
    return map
  }, [userStandardsList, acceptedApplicabilityWithUserStandard])

  const standardsForHazards = useMemo((): UserStandard[] => {
    return acceptedApplicabilityWithUserStandard
      .map((applicableStandard) => {
        const userStandard =
          applicableStandard.userStandardId != null
            ? userStandardsById.get(applicableStandard.userStandardId)
            : undefined
        return userStandard ?? null
      })
      .filter((standard): standard is UserStandard => standard != null)
  }, [acceptedApplicabilityWithUserStandard, userStandardsById])

  const items = useMemo(
    () =>
      buildStandardsHazardPreviewItems(
        standardsForHazards,
        acceptedApplicabilityWithUserStandard.map(
          (applicableStandard) => applicableStandard.reference
        ),
        displayLocale
      ),
    [
      standardsForHazards,
      acceptedApplicabilityWithUserStandard,
      displayLocale,
    ]
  )
  const grouped = useMemo(() => groupByStandard(items), [items])

  const isLoading =
    isLoadingApplicability ||
    (acceptedApplicabilityWithUserStandard.length > 0 && isLoadingUserStandards)
  const error = applicabilityError

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className='flex max-h-[85vh] flex-col gap-4 sm:max-w-3xl'
        showCloseButton={!isGenerating}
      >
        {isOpen ? (
          <GenerateFromStandardsModalBody
            grouped={grouped}
            items={items}
            acceptedApplicability={acceptedApplicabilityWithUserStandard}
            isLoading={isLoading}
            error={error}
            isGenerating={isGenerating}
            onGenerate={onGenerate}
            onClose={onClose}
            generatedSignificantHazardIds={generatedSignificantHazardIds}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

type ModalBodyProps = {
  grouped: Map<string, StandardsHazardPreviewItem[]>
  items: StandardsHazardPreviewItem[]
  acceptedApplicability: StandardApplicability[]
  isLoading: boolean
  error: Error | null
  isGenerating: boolean
  onGenerate: (selectedHazardIds: string[]) => void
  onClose: () => void
  generatedSignificantHazardIds: ReadonlySet<string>
}

function selectableIdsForGroup(groupItems: StandardsHazardPreviewItem[]) {
  return groupItems.map((item) => item.id).filter((id) => id !== '')
}

function GenerateFromStandardsModalBody({
  grouped,
  items,
  acceptedApplicability,
  isLoading,
  error,
  isGenerating,
  onGenerate,
  onClose,
  generatedSignificantHazardIds,
}: ModalBodyProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [expandedStandards, setExpandedStandards] = useState<Set<string>>(
    new Set()
  )
  const [expandedHazardRows, setExpandedHazardRows] = useState<Set<string>>(
    new Set()
  )

  const toggleHazardRowExpanded = useCallback((rowKey: string) => {
    setExpandedHazardRows((prev) => {
      const next = new Set(prev)
      if (next.has(rowKey)) {
        next.delete(rowKey)
      } else {
        next.add(rowKey)
      }
      return next
    })
  }, [])

  const toggleStandardExpanded = useCallback((standardRef: string) => {
    setExpandedStandards((prev) => {
      const next = new Set(prev)
      if (next.has(standardRef)) {
        next.delete(standardRef)
      } else {
        next.add(standardRef)
      }
      return next
    })
  }, [])

  const toggleId = useCallback((id: string) => {
    if (id === '') {
      return
    }
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const toggleStandard = useCallback(
    (standardRef: string) => {
      const groupItems = grouped.get(standardRef) ?? []
      const ids = selectableIdsForGroup(groupItems)
      if (ids.length === 0) {
        return
      }
      const allSelected = ids.every((id) => selectedIds.has(id))
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (allSelected) {
          ids.forEach((id) => next.delete(id))
        } else {
          ids.forEach((id) => next.add(id))
        }
        return next
      })
    },
    [grouped, selectedIds]
  )

  const selectedList = useMemo(() => Array.from(selectedIds), [selectedIds])
  const canGenerate = selectedList.length > 0 && !isGenerating

  const handleGenerate = useCallback(() => {
    if (!canGenerate) {
      return
    }
    onGenerate(selectedList)
    onClose()
  }, [canGenerate, selectedList, onGenerate, onClose])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const listRows = useMemo(() => {
    const rows: Array<{
      key: string
      sa: StandardApplicability
      groupItems: StandardsHazardPreviewItem[]
    }> = []
    for (const sa of acceptedApplicability) {
      const groupItems = grouped.get(sa.reference) ?? []
      if (groupItems.length > 0) {
        rows.push({ key: sa.id, sa, groupItems })
        continue
      }
      rows.push({ key: sa.id, sa, groupItems: [] })
    }
    return rows
  }, [acceptedApplicability, grouped])

  const hasAnyHazards = items.length > 0

  const fieldLabels = useMemo(
    () => ({
      examplesOfHazardousSituations: t(
        'hazardsPage.generateFromStandardsModal.examplesOfHazardousSituations'
      ),
      associatedActivity: t(
        'hazardsPage.generateFromStandardsModal.associatedActivity'
      ),
      relatedDangerZone: t(
        'hazardsPage.generateFromStandardsModal.relatedDangerZone'
      ),
      clauseReference: t(
        'hazardsPage.generateFromStandardsModal.clauseReference'
      ),
    }),
    [t]
  )

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {t('hazardsPage.generateFromStandardsModal.title')}
        </DialogTitle>
      </DialogHeader>

      <div className='min-h-0 flex-1 space-y-4 overflow-y-auto'>
        {isLoading && (
          <div className='flex justify-center py-8'>
            <Skeleton className='size-10 rounded-full' />
          </div>
        )}
        {error != null && (
          <p className='text-sm text-destructive'>
            {t('hazardsPage.generateFromStandardsModal.loadFailed', {
              message: error.message,
            })}
          </p>
        )}
        {!isLoading && error == null && listRows.length === 0 && (
          <p className='py-6 text-center text-muted-foreground'>
            {acceptedApplicability.length === 0
              ? t('hazardsPage.generateFromStandardsModal.noHazardsFound')
              : t(
                  'hazardsPage.generateFromStandardsModal.noHazardsButHasStandards'
                )}
          </p>
        )}
        {!isLoading && error == null && listRows.length > 0 && (
          <div className='space-y-4'>
            {hasAnyHazards && (
              <p className='text-sm text-muted-foreground'>
                {t('hazardsPage.generateFromStandardsModal.selectHint')}
              </p>
            )}
            <div className='space-y-4'>
              {listRows.map(({ key, sa, groupItems }) => {
                const standardReference = sa.reference
                if (groupItems.length === 0) {
                  const showDataStatusBadge = applicabilityNeedsDataBadge(sa)
                  return (
                    <div key={key} className='rounded-md border'>
                      <div className='flex flex-wrap items-center gap-2 px-3 py-2'>
                        <span className='min-w-0 flex-1 text-sm font-medium'>
                          {standardReference}
                        </span>
                        {showDataStatusBadge ? (
                          <StandardDataStatusBadge
                            hasData={
                              sa.hasSignificantHazards === true ||
                              sa.hasInformationForUse === true ||
                              sa.hasRiskReductionMeasures === true
                            }
                            hasPdf={sa.hasFile === true}
                            requireLinkedUserStandard
                            userStandardId={sa.userStandardId}
                          />
                        ) : (
                          <span className='shrink-0 text-xs text-muted-foreground'>
                            {t(
                              'hazardsPage.generateFromStandardsModal.hazardsCount',
                              { count: 0 }
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                }
                const selectable = selectableIdsForGroup(groupItems)
                const allSelected =
                  selectable.length > 0 &&
                  selectable.every((id) => selectedIds.has(id))
                const someSelected = selectable.some((id) =>
                  selectedIds.has(id)
                )
                const isStandardExpanded =
                  expandedStandards.has(standardReference)
                return (
                  <div key={key} className='rounded-md border'>
                    <div className='flex items-center gap-2 px-3 py-2'>
                      <Checkbox
                        id={`standard-${standardReference}`}
                        checked={allSelected}
                        disabled={selectable.length === 0}
                        onCheckedChange={() =>
                          toggleStandard(standardReference)
                        }
                      />
                      <button
                        type='button'
                        className='flex min-w-0 flex-1 items-center gap-1 text-left text-sm font-medium hover:text-foreground'
                        onClick={() =>
                          toggleStandardExpanded(standardReference)
                        }
                        aria-expanded={isStandardExpanded}
                      >
                        <ChevronDown
                          className={cn(
                            'size-5 shrink-0 text-muted-foreground',
                            {
                              'rotate-180': isStandardExpanded,
                            }
                          )}
                        />
                        <span className='min-w-0'>{standardReference}</span>
                      </button>
                      <span className='shrink-0 text-xs text-muted-foreground'>
                        {someSelected
                          ? t(
                              'hazardsPage.generateFromStandardsModal.selectedCount',
                              {
                                selected: selectable.filter((id) =>
                                  selectedIds.has(id)
                                ).length,
                                total: selectable.length,
                              }
                            )
                          : t(
                              'hazardsPage.generateFromStandardsModal.hazardsCount',
                              { count: groupItems.length }
                            )}
                      </span>
                    </div>
                    <Collapse open={isStandardExpanded}>
                      <div className='divide-y border-t'>
                        {groupItems.map((item) => {
                          const rowKey = `${standardReference}-${item.index}`
                          const selectableRow = item.id !== ''
                          const alreadyGenerated =
                            selectableRow &&
                            generatedSignificantHazardIds.has(item.id)
                          const hasHazardNumber =
                            item.number != null && item.number !== ''
                          const isHazardRowExpanded =
                            expandedHazardRows.has(rowKey)
                          let hazardTitle = ''
                          if (hasHazardNumber) {
                            hazardTitle = `${item.number}${item.category != null && item.category !== '' ? ` — ${item.category}` : ''}`
                          } else if (isHazardRowExpanded) {
                            const category = item.category?.trim() ?? ''
                            hazardTitle =
                              category !== '' ? category : item.description
                          } else {
                            hazardTitle = item.description
                          }
                          const showDescriptionInDetails =
                            item.description !== '' &&
                            (hasHazardNumber ||
                              (item.category?.trim() ?? '') !== '')
                          return (
                            <div
                              key={rowKey}
                              className='px-3 py-2 hover:bg-muted/40'
                            >
                              <div className='flex w-full items-start gap-2'>
                                <div className='shrink-0 pt-0.5'>
                                  <Checkbox
                                    id={`hazard-${rowKey}`}
                                    checked={selectedIds.has(item.id)}
                                    disabled={!selectableRow}
                                    onCheckedChange={() => toggleId(item.id)}
                                  />
                                </div>
                                <button
                                  type='button'
                                  className='flex min-w-0 flex-1 items-start gap-1 text-left hover:text-foreground'
                                  onClick={() =>
                                    toggleHazardRowExpanded(rowKey)
                                  }
                                  aria-expanded={isHazardRowExpanded}
                                >
                                  <ChevronDown
                                    className={cn(
                                      'mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform',
                                      {
                                        'rotate-180': isHazardRowExpanded,
                                      }
                                    )}
                                  />
                                  <div className='flex min-w-0 flex-1 flex-wrap items-start justify-between gap-2 text-sm font-medium'>
                                    <span
                                      className={cn('min-w-0', {
                                        'line-clamp-2':
                                          !hasHazardNumber &&
                                          !isHazardRowExpanded,
                                      })}
                                    >
                                      {hazardTitle}
                                    </span>
                                    {alreadyGenerated ? (
                                      <Badge
                                        variant='secondary'
                                        className='shrink-0 font-normal'
                                      >
                                        {t(
                                          'hazardsPage.generateFromStandardsModal.alreadyGenerated'
                                        )}
                                      </Badge>
                                    ) : null}
                                  </div>
                                </button>
                              </div>
                              <Collapse
                                open={isHazardRowExpanded}
                                className='pl-7 pt-0'
                              >
                                <div className='space-y-2 pt-2'>
                                  {showDescriptionInDetails ? (
                                    <ExpandableItemDescription
                                      description={item.description}
                                      showMoreLabel={t(
                                        'hazardsPage.generateFromStandardsModal.showMore'
                                      )}
                                      showLessLabel={t(
                                        'hazardsPage.generateFromStandardsModal.showLess'
                                      )}
                                    />
                                  ) : null}
                                  <CatalogueHazardExtraFields
                                    item={item}
                                    fieldLabels={fieldLabels}
                                  />
                                  {!selectableRow ? (
                                    <p className='text-xs text-muted-foreground'>
                                      {t(
                                        'hazardsPage.generateFromStandardsModal.missingIdHint'
                                      )}
                                    </p>
                                  ) : null}
                                </div>
                              </Collapse>
                            </div>
                          )
                        })}
                      </div>
                    </Collapse>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant='outline' onClick={handleClose} disabled={isGenerating}>
          {t('common:actions.cancel')}
        </Button>
        <Button disabled={!canGenerate} onClick={handleGenerate}>
          {t('hazardsPage.generateFromStandardsModal.generateSelected', {
            count: selectedList.length,
          })}
        </Button>
      </DialogFooter>
    </>
  )
}
